import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState,
} from "react";
import {
  getAppointments,
  getAppointmentAdvisors,
  getAppointmentVehicles,
} from "../../../../services/appointments";

// Helper function to check if body is empty
const isBodyEmpty = (body) => {
  if (!body) return true;
  if (Array.isArray(body)) return body.length === 0;
  if (typeof body === 'object') return Object.keys(body).length === 0;
  return false;
};

const AppointmentsContext = createContext();

const initialState = {
  baseAppointments: [],
  baseMeta: null,
  basePartyUuid: null,
  appointments: [],
  appointmentsMeta: null,
  advisors: [],
  vehicles: [],
  loading: false,
  filtersLoading: false,
  error: null,
  isEmpty: false,
};

export const AppointmentsProvider = ({ children }) => {
  const [state, setState] = useState(initialState);
  const fetchRequestIdRef = useRef(0);

  const setPartialState = useCallback((updates) => {
    setState((prev) => ({ ...prev, ...updates }));
  }, []);

  const fetchAppointments = useCallback(
    async (params = {}, options = {}) => {
      const { persistAsBase = false } = options;
      const requestId = ++fetchRequestIdRef.current;

      setState((prev) => ({
        ...prev,
        loading: true,
        error: null,
        isEmpty: false,
      }));

      try {
        const response = await getAppointments(params);

        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        const responseStatusCode = response?.statusCode;
        const isValidResponse = responseStatusCode === 200 || responseStatusCode === 201;

        if (!isValidResponse) {
          const errorMessage = response?.msgCode?.[0]?.description || 'Invalid response status code';
          throw new Error(errorMessage);
        }

        const body = response?.body;
        
        if (isBodyEmpty(body)) {
          setState((prev) => ({
            ...prev,
            baseAppointments: persistAsBase ? [] : prev.baseAppointments,
            baseMeta: persistAsBase ? null : prev.baseMeta,
            basePartyUuid: persistAsBase ? null : prev.basePartyUuid,
            appointments: [],
            appointmentsMeta: response.meta || null,
            loading: false,
            error: null,
            isEmpty: true,
          }));
        } else {
          const appointments = Array.isArray(body) ? body : [];
          const meta = response.meta || null;
          const partyUuid = params?.party_uuid ?? null;

          setState((prev) => ({
            ...prev,
            baseAppointments: persistAsBase ? appointments : prev.baseAppointments,
            baseMeta: persistAsBase ? meta : prev.baseMeta,
            basePartyUuid: persistAsBase ? partyUuid : prev.basePartyUuid,
            appointments,
            appointmentsMeta: meta,
            loading: false,
            error: null,
            isEmpty: false,
          }));
        }
      } catch (error) {
        if (requestId !== fetchRequestIdRef.current) {
          setState((prev) => ({
            ...prev,
            loading: false,
          }));
          return;
        }

        console.error("Error fetching appointments:", error);
        setState((prev) => ({
          ...prev,
          loading: false,
          error,
          appointments: [],
          appointmentsMeta: null,
          isEmpty: false,
          ...(persistAsBase
            ? {
                baseAppointments: [],
                baseMeta: null,
                basePartyUuid: null,
              }
            : {}),
        }));
      }
    },
    []
  );

  const fetchAdvisors = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getAppointmentAdvisors(partyUuid, search);
        setPartialState({
          advisors: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching appointment advisors:", error);
        setPartialState({
          advisors: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const fetchVehicles = useCallback(
    async (partyUuid, search = null) => {
      setPartialState({ filtersLoading: true });
      try {
        const response = await getAppointmentVehicles(partyUuid, search);
        setPartialState({
          vehicles: Array.isArray(response.body) ? response.body : [],
          filtersLoading: false,
        });
      } catch (error) {
        console.error("Error fetching appointment vehicles:", error);
        setPartialState({
          vehicles: [],
          filtersLoading: false,
        });
      }
    },
    [setPartialState]
  );

  const setFilteredAppointments = useCallback((appointments = [], meta = null) => {
    setState((prev) => ({
      ...prev,
      appointments: Array.isArray(appointments) ? appointments : [],
      appointmentsMeta: meta || null,
    }));
  }, []);

  const resetAppointmentsToBase = useCallback(() => {
    
    fetchRequestIdRef.current = fetchRequestIdRef.current + 1;
    
    setState((prev) => ({
      ...prev,
      appointments: prev.baseAppointments,
      appointmentsMeta: prev.baseMeta,
      loading: false, 
    }));
  }, []);

  const resetAppointments = useCallback(() => {
    setState(initialState);
    fetchRequestIdRef.current = 0;
  }, []);

  const value = {
    baseAppointments: state.baseAppointments,
    baseMeta: state.baseMeta,
    basePartyUuid: state.basePartyUuid,
    appointments: state.appointments,
    appointmentsMeta: state.appointmentsMeta,
    advisors: state.advisors,
    vehicles: state.vehicles,
    loading: state.loading,
    filtersLoading: state.filtersLoading,
    error: state.error,
    isEmpty: state.isEmpty,
    fetchAppointments,
    fetchAdvisors,
    fetchVehicles,
    setFilteredAppointments,
    resetAppointmentsToBase,
    resetAppointments,
  };

  return (
    <AppointmentsContext.Provider value={value}>
      {children}
    </AppointmentsContext.Provider>
  );
};

export const useAppointments = () => {
  const context = useContext(AppointmentsContext);
  if (!context) {
    throw new Error("useAppointments must be used within AppointmentsProvider");
  }
  return context;
};

